import { Button } from "@tokenterminal/core/ui/button/Button"
import { Icon } from "@tokenterminal/core/ui/icon/Icon"
import { Modal } from "@tokenterminal/core/ui/overlays/modal/Modal"
import { formatNumber } from "@tokenterminal/core/utils/sharedFormatter"
import Link from "next/link"

import { isOtherGroupItem, type TreemapItem } from "./useTreemapData"

type AllItemsModalProps = {
  treeMapData: TreemapItem[]
  colorMode: "trend" | "categorical"
  metricFormat: string
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
}
type ItemRowProps = {
  name: React.ReactNode
  slug?: string
  value?: number
  change?: number | null
  marketShare?: number
  icon?: React.ReactNode
  badge?: React.ReactNode
  metricFormat: string
}

const ItemRow = ({
  name,
  slug,
  value,
  change,
  marketShare,
  icon,
  badge,
  metricFormat,
}: ItemRowProps) => {
  const Container = slug
    ? (props: any) => <Link href={`/markets/${slug}`} {...props} />
    : (props: any) => <div {...props} />

  return (
    <Container
      className={`grid grid-cols-8 items-center gap-4 px-3 py-2 border-b border-solid border-border-default last:border-b-0 ${slug ? "hover:bg-neutral-950/5 dark:hover:bg-neutral-50/5" : ""}`}
    >
      {slug ? (
        <Link
          href={`/markets/${slug}`}
          className="flex items-center gap-2 col-span-3"
        >
          {icon}
          <span>{name}</span>
        </Link>
      ) : (
        <div className="flex items-center gap-2 col-span-3">
          {icon}
          <span>{name}</span>
        </div>
      )}
      <div className="text-right col-span-2">
        {value &&
          formatNumber({
            value,
            format: "compact",
            metricFormat,
          })}
      </div>
      <div>{badge}</div>
      <div className="text-fg-secondary text-right text-sm col-span-2">
        {marketShare && `${(marketShare * 100).toFixed(2)}%`}
      </div>
    </Container>
  )
}

const ChangeBadge = ({ change }: { change: number | null }) =>
  change !== null && (
    <div
      className={`!py-0 !gap-0 tabular-nums tracking-wider ${change > 0 ? "text-fg-emerald" : change < 0 ? "text-fg-red" : "text-fg-secondary"} tabular-nums !font-normal`}
    >
      {change > 0 && <span className="mr-px">+</span>}
      {change < 0 && <span className="mr-px">-</span>}
      {Math.abs(change).toFixed(1)}%
    </div>
  )

export const AllItemsModal = ({
  isOpen,
  setIsOpen,
  treeMapData,
  colorMode,
  metricFormat,
}: AllItemsModalProps) => {
  const showAllItems = false

  if (showAllItems) {
    return (
      <Modal isOpen={isOpen} onOpenChange={setIsOpen}>
        <Modal.Header title="Market Sectors" subtitle="Breakdown" />
        <Modal.Body>
          <div>
            <div className="grid grid-cols-5 gap-2 mb-2 px-0 text-fg-secondary text-sm">
              <div className="col-span-2">Sector</div>
              <div className="text-right">
                {metricFormat === "currency" ? "Value" : "Amount"}
              </div>
              <div>Market Share</div>
              <div>Change</div>
            </div>
            <div className="flex flex-col border border-solid border-border-default rounded-lg overflow-hidden">
              {treeMapData.map((item, index) => {
                if (item.type === "regular") {
                  return (
                    <ItemRow
                      key={item.name}
                      name={item.name}
                      slug={item.slug}
                      value={item.originalValue}
                      change={item.change}
                      marketShare={item.percentageOfTotal}
                      icon={
                        item.icon ? (
                          <Icon as={item.icon} className="opacity-75" />
                        ) : null
                      }
                      badge={<ChangeBadge change={item.change} />}
                      metricFormat={metricFormat}
                    />
                  )
                } else {
                  // This is the "Other" group item
                  return (
                    <>
                      <ItemRow
                        key={item.name}
                        name={
                          <div className="flex items-center gap-2">
                            Other ({item.otherGroupCount} items)
                          </div>
                        }
                        slug={undefined}
                        icon={null}
                        metricFormat={metricFormat}
                      />
                      <div className="bg-bg-inset">
                        <div className="flex flex-col border-l border-solid border-border-default overflow-hidden ml-6">
                          {item.otherChildren.map((otherItem, otherIndex) => {
                            if (otherItem.type === "regular") {
                              return (
                                <ItemRow
                                  key={otherItem.name}
                                  name={otherItem.name}
                                  slug={otherItem.slug}
                                  value={otherItem.originalValue}
                                  change={otherItem.change}
                                  marketShare={otherItem.percentageOfTotal}
                                  icon={
                                    <Icon
                                      as={otherItem.icon}
                                      className="opacity-75"
                                    />
                                  }
                                  badge={
                                    <ChangeBadge change={otherItem.change} />
                                  }
                                  metricFormat={metricFormat}
                                />
                              )
                            }
                            return null
                          })}
                        </div>
                      </div>
                    </>
                  )
                }
              })}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="justify-end">
          <Button onPress={() => setIsOpen(false)}>Done</Button>
        </Modal.Footer>
      </Modal>
    )
  }

  return (
    <Modal isOpen={isOpen} onOpenChange={setIsOpen}>
      <Modal.Header
        title={`${treeMapData.find(isOtherGroupItem)?.otherChildren.length} other market sectors`}
      />
      <Modal.Body>
        <div>
          <div className="grid grid-cols-8 gap-4 mb-2 px-0 text-fg-secondary text-sm">
            <div className="col-span-3">Sector</div>
            <div className="text-right col-span-2">
              {metricFormat === "currency" ? "Value" : "Amount"}
            </div>
            <div>Change</div>
            <div className="text-right col-span-2">Market Share</div>
          </div>
          <div className="flex flex-col border border-solid border-border-default rounded-lg overflow-hidden">
            {treeMapData
              .filter(isOtherGroupItem)
              .flatMap((item) => item.otherChildren)
              .map((otherItem, otherIndex) => {
                if (otherItem.type !== "regular") return null

                return (
                  <ItemRow
                    key={otherItem.name}
                    name={otherItem.name}
                    slug={otherItem.slug}
                    value={otherItem.originalValue}
                    change={otherItem.change}
                    marketShare={otherItem.percentageOfTotal}
                    icon={<Icon as={otherItem.icon} className="opacity-75" />}
                    badge={<ChangeBadge change={otherItem.change} />}
                    metricFormat={metricFormat}
                  />
                )
              })}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="justify-end">
        <Button onPress={() => setIsOpen(false)}>Done</Button>
      </Modal.Footer>
    </Modal>
  )
}
